import { useMemo, useState } from "react";
import type { ClozeBlock } from "@shared/lessonBlocks";
import { normalizeAnswerText } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, shuffled, type CheckResult } from "../CheckShell";

interface Props {
  block: ClozeBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

type Segment = { kind: "text"; text: string } | { kind: "blank"; index: number; answer: string };

function parse(text: string): Segment[] {
  const out: Segment[] = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    out.push({ kind: "blank", index: i++, answer: m[1].trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Tap a word from the bank to drop it into the highlighted blank. Tap a filled blank to clear it. */
export function ClozeBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const segments = useMemo(() => parse(block.text), [block.text]);
  const blanks = segments.filter((s): s is Extract<Segment, { kind: "blank" }> => s.kind === "blank");
  const bank = useMemo(() => {
    const words = [...blanks.map(b => b.answer), ...(block.distractors || [])];
    return shuffled(words.map((w, i) => ({ id: `w${i}`, word: w })));
  }, [block.id]);
  const [fills, setFills] = useState<Record<number, string>>({}); // blank index -> bank word id
  const [active, setActive] = useState<number>(0);
  const [wrong, setWrong] = useState<Set<number>>(new Set());
  const done = check.locked;
  const usedIds = new Set(Object.values(fills));
  const wordOf = (id: string) => bank.find(w => w.id === id)?.word || "";

  const nextEmpty = (from: number, f: Record<number, string>) => {
    for (let k = 0; k < blanks.length; k++) {
      const idx = (from + k) % blanks.length;
      if (!f[idx]) return idx;
    }
    return -1;
  };
  const pick = (wordId: string) => {
    if (done || usedIds.has(wordId)) return;
    if (check.status === "wrong") check.retry();
    let target = active;
    if (fills[target]) target = nextEmpty(active, fills);
    if (target < 0) return;
    const next = { ...fills, [target]: wordId };
    setFills(next);
    setWrong(prev => { const n = new Set(prev); n.delete(target); return n; });
    setActive(nextEmpty(target + 1, next) >= 0 ? nextEmpty(target + 1, next) : target);
  };
  const clear = (idx: number) => {
    if (done) return;
    if (check.status === "wrong") check.retry();
    setFills(prev => { const n = { ...prev }; delete n[idx]; return n; });
    setWrong(prev => { const n = new Set(prev); n.delete(idx); return n; });
    setActive(idx);
  };
  const allFilled = blanks.every(b => fills[b.index]);
  const submit = () => {
    const bad = new Set<number>();
    for (const b of blanks) {
      if (normalizeAnswerText(wordOf(fills[b.index])) !== normalizeAnswerText(b.answer)) bad.add(b.index);
    }
    setWrong(bad);
    check.submit(bad.size === 0);
  };
  const revealed = done && check.status === "revealed";

  return (
    <div className="space-y-4">
      {block.prompt && <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />}
      <div className="rounded-2xl border border-border bg-card p-4 text-lg leading-loose">
        {segments.map((seg, i) => {
          if (seg.kind === "text") return <RichText key={i} text={seg.text} inline />;
          const filled = revealed ? seg.answer : fills[seg.index] ? wordOf(fills[seg.index]) : "";
          const isActive = active === seg.index && !done;
          const isWrong = wrong.has(seg.index) && !done;
          const cls = done
            ? (check.status === "correct" || revealed || normalizeAnswerText(filled) === normalizeAnswerText(seg.answer) ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30" : "border-rose-400 bg-rose-50 dark:bg-rose-900/30")
            : isWrong ? "border-rose-400 bg-rose-50 dark:bg-rose-900/30"
            : isActive ? "border-primary bg-primary/10 ring-2 ring-primary/30"
            : "border-border bg-muted/40";
          return (
            <button
              key={i}
              type="button"
              disabled={done}
              onClick={() => (fills[seg.index] ? clear(seg.index) : setActive(seg.index))}
              className={`inline-flex items-center justify-center min-w-[84px] mx-1 px-3 py-0.5 rounded-xl border-2 border-dashed font-bold align-baseline transition-colors ${cls}`}
              data-testid={`cloze-blank-${seg.index}`}
            >
              {filled || <span className="text-muted-foreground font-normal">____</span>}
            </button>
          );
        })}
      </div>
      {!revealed && (
        <div className="flex flex-wrap gap-2">
          {bank.map(w => (
            <button
              key={w.id}
              type="button"
              disabled={done || usedIds.has(w.id)}
              onClick={() => pick(w.id)}
              className={`rounded-xl border-2 px-3 py-2 font-semibold transition-all ${usedIds.has(w.id) ? "opacity-30 border-border bg-muted line-through" : "border-primary/40 bg-primary/5 hover:bg-primary/15 active:scale-95"}`}
              data-testid={`cloze-word-${w.id}`}
            >
              {w.word}
            </button>
          ))}
        </div>
      )}
      <CheckFooter
        check={check}
        canSubmit={allFilled}
        onSubmit={submit}
        feedbackWrong={wrong.size ? `${wrong.size} ${wrong.size === 1 ? "blank is" : "blanks are"} not right. Tap a red blank to change it.` : undefined}
        revealAnswer={<p>The blanks now show the correct words.</p>}
      />
    </div>
  );
}
